/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Main component for Calls Detail
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.4.0
 */

import { useCallback, useEffect } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';

import { EBasketType } from '../../../modules/app/appConstants';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../../modules/internationalization/constants';
import { makeSelectCallsDetailTranslation } from '../../../modules/internationalization/intSelectors';
import { ICallsDetailTranslation } from '../../../modules/internationalization/types/wizards/callsDetail';
import Wizard from '../../../modules/wizard/components/Wizard';
import { EWizardName, WIZARD_ALL_ENTITIES } from '../../../modules/wizard/wizardConstants';
import { makeSelectWizardStepName } from '../../../modules/wizard/wizardSelectors';
import { TWizardStepNames } from '../../../modules/wizard/wizardTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { IRouteParams } from '../../../routes/constants/routesConstants';
import { callsDetailHandleNext } from '../callsDetailActions';
import { ECallsDetailSteps } from '../callsDetailConstants';
import { makeSelectCallsDetailIsStepValid } from '../callsDetailSelectors';
import WizardCallsDetailStepFinal from './WizardCallsDetailStepFinal';
import WizardCallsDetailStepPrepare from './WizardCallsDetailStepPrepare';

interface IWizardCallsDetailProps {
    basketType: EBasketType;
}

export interface ICallsDetailMatchProps {
    subscriberId: string;
}

export type TWizardCallsDetail = IWizardCallsDetailProps & IConnectedProps & IDispatchProps &
    RouteComponentProps<ICallsDetailMatchProps>;

export const WizardCallsDetail =
    ({
        stepName,
        logBusinessEvent,
        basketType,
        callsDetailTranslation,
        isStepValid,
        handleNext,
     } : TWizardCallsDetail) => {
    const { subscriberId } = useParams<IRouteParams>();

    useEffect(() => {
        switch (stepName) {
            case ECallsDetailSteps.Prepare:
                logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_CALL_DETAILS_MASTER_ON_STEP_1,
                    type: ELogBusinessEventType.common,
                });
                break;
            case ECallsDetailSteps.Final:
                logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_CALL_DETAILS_MASTER_ON_STEP_2,
                    type: ELogBusinessEventType.common,
                });
                break;
        }
    },[stepName, logBusinessEvent])

    const renderStep = useCallback(() => {
        switch (stepName) {
            case ECallsDetailSteps.Prepare:
                return <WizardCallsDetailStepPrepare/>;
            case ECallsDetailSteps.Final:
                return <WizardCallsDetailStepFinal/>;
        }
    }, [stepName]);

    return <PageLocale fileNames={[
        TranslationFileNamesEnum.common,
        TranslationFileNamesEnum.callsDetail,
        TranslationFileNamesEnum.wizard,
    ]}>
        <Wizard
            basketType={basketType}
            entityId={subscriberId ? parseInt(subscriberId) : WIZARD_ALL_ENTITIES}
            isNextDisabled={!isStepValid}
            name={EWizardName.CALLS_DETAIL}
            translationProps={{
                error: callsDetailTranslation.error,
                steps: callsDetailTranslation.steps,
                title: callsDetailTranslation.title,
            }}
            onWizardNextHandler={handleNext}
        >
            {renderStep()}
        </Wizard>
    </PageLocale>;
};

interface IConnectedProps {
    isStepValid: boolean;
    callsDetailTranslation: ICallsDetailTranslation;
    stepName: TWizardStepNames;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isStepValid: makeSelectCallsDetailIsStepValid(),
    callsDetailTranslation: makeSelectCallsDetailTranslation(),
    stepName: makeSelectWizardStepName(),
});

interface IDispatchProps {
    handleNext: () => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(callsDetailHandleNext()),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardCallsDetail);
